import React, { useEffect } from 'react';
import FilerobotImageEditor, { TABS, TOOLS } from 'react-filerobot-image-editor';
import './ImageEditor.css';

/** The subset of the editor's saved-image payload this app relies on. */
interface SavedImage {
  name: string;
  extension: string;
  mimeType: string;
  fullName?: string;
  imageBase64?: string;
}

interface ImageEditorProps {
  source: string;
  /**
   * Name offered in the "Save as" dialog. Keep the extension on it: the editor
   * trims the last character of an extension-less name that is 3-4 characters long.
   */
  fileName: string;
  onClose: () => void;
}

/**
 * The editor is a third-party component: its layout is fixed, but its palette
 * and typography are themeable. Token names come from @scaleflex/ui.
 */
const EDITOR_THEME = {
  palette: {
    'accent-primary': '#D91A1A',
    'accent-primary-hover': '#B31313',
    'accent-primary-active': '#B31313',
    'bg-primary': '#F2EEE4',
    'bg-primary-active': '#FBE9E9',
    'bg-primary-hover': '#E9E4D8',
    'bg-secondary': '#FFFFFF',
    'txt-primary': '#121110',
    'txt-secondary': '#56524B',
    'txt-placeholder': '#8B857A',
    'icons-primary': '#121110',
    'icons-secondary': '#56524B',
    'borders-primary': '#C9C2B4',
    'borders-secondary': '#DED8CA',
    'borders-strong': '#121110',
    'link-primary': '#D91A1A',
    'link-hover': '#B31313',
  },
  typography: {
    fontFamily: "'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
};

const CROP_PRESETS = [
  { titleKey: 'original', descriptionKey: 'Original' },
  { titleKey: 'square', descriptionKey: '1:1', ratio: 1 },
  { titleKey: 'classicTv', descriptionKey: '4:3', ratio: 4 / 3 },
  { titleKey: 'wideScreen', descriptionKey: '16:9', ratio: 16 / 9 },
  { titleKey: 'story', descriptionKey: '9:16', ratio: 9 / 16 },
];

const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, base64] = dataUrl.split(',');
  const mimeType = /:(.*?);/.exec(header)?.[1] ?? 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mimeType });
};

/** Keeps the browser from choking on a name built out of a long data URL. */
const toSafeFileName = (image: SavedImage) => {
  const name = (image.fullName ?? `${image.name}.${image.extension}`)
    .replace(/[/\\]+/g, '-')
    .slice(-100);

  return name || `edited-image.${image.extension}`;
};

/**
 * Downloads through an object URL rather than the raw data URL: mobile Safari
 * ignores the `download` attribute on very long `data:` hrefs.
 */
const downloadImage = (image: SavedImage) => {
  if (!image.imageBase64) {
    return;
  }

  const objectUrl = URL.createObjectURL(dataUrlToBlob(image.imageBase64));
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = toSafeFileName(image);
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking in the same tick cancels the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const ImageEditor = ({ source, fileName, onClose }: ImageEditorProps) => {
  // The editor fills the viewport, so stop the page behind it from scrolling.
  useEffect(() => {
    document.body.classList.add('is-editing');
    return () => document.body.classList.remove('is-editing');
  }, []);

  return (
    <div className="image-editor">
      <FilerobotImageEditor
        source={source}
        theme={EDITOR_THEME}
        onSave={downloadImage}
        onClose={onClose}
        defaultSavedImageName={fileName}
        // Ship with the bundled English strings instead of fetching them.
        useBackendTranslations={false}
        annotationsCommon={{ fill: '#D91A1A' }}
        Rotate={{ angle: 90, componentType: 'slider' }}
        Crop={{ presetsItems: CROP_PRESETS }}
        tabsIds={[
          TABS.ADJUST,
          TABS.FINETUNE,
          TABS.FILTERS,
          TABS.ANNOTATE,
          TABS.WATERMARK,
          TABS.RESIZE,
        ]}
        defaultTabId={TABS.ADJUST}
        defaultToolId={TOOLS.CROP}
        savingPixelRatio={1}
        previewPixelRatio={1}
      />
    </div>
  );
};

export default ImageEditor;
